import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import chatRoomKey from '~/features/chatRoom/chatRoomKey';
import {
  ChatAvatarData,
  ChatJoinRequest,
  ErrorMessageResponse,
  GroupChatJoinRequest,
  MessageResponse,
  MissionGameChatJoinRequest,
} from '~/types';
import { SendMessage } from '~/utils';
import { MINUTE, SECOND } from '~/constants';
import { useChatAvatarStore, useCommonChatStore } from '~/store';
import { useInterval } from '../useInterval';

const ENTERING_MESSAGE_ID = 'system_message';

export type ReceivedExpression = {
  value: Record<string, string[]>;
  duration: string;
  name: string;
  type: string;
};

export type EnteringType = 'enter' | 'leave';

// 일반 컨셉 채팅 혹은 그룹 챗 Socket Message Type
export type SendMessageResponse = {
  chat_id: string;
  id: MessageResponse['id'];
  msg: MessageResponse['msg'];
  send_time: string;
};

export type ChatMessageResponse = {
  id: MessageResponse['id'];
  send_time: string;
  msg: MessageResponse['msg'];
  expression: ReceivedExpression;
  motion_url?: string;
  chatroom_uuid?: string;
  groupchat_uuid?: string;
};

// 미션 게임 서비스 Socket Message Type
type SendGameMessageResponse = Omit<SendMessageResponse, 'chat_id'> & {
  game_chat_id: string;
};

type ChatGameMessageResponse = Pick<
  ChatMessageResponse,
  'send_time' | 'msg' | 'expression' | 'motion_url'
> & {
  chat_id: number;
  character_id: string;
};

export type EnteringMessageResponse = {
  type: EnteringType;
  nickname: string;
  email: string;
};

export type SocketParsedMessage =
  | SendMessageResponse
  | SendGameMessageResponse
  | ChatMessageResponse
  | ChatGameMessageResponse
  | ErrorMessageResponse
  | EnteringMessageResponse;

interface useWebSocketProps {
  chatRoomId?: string;
  chatType: 'group' | 'personal' | 'mission';
  fetchDone: boolean;
  email: string;
  setMessages: React.Dispatch<React.SetStateAction<MessageResponse[]>>;
  socketConnectSuccess: boolean;
  setSocketConnectSuccess: React.Dispatch<React.SetStateAction<boolean>>;
}

const useWebSocket = ({
  chatRoomId,
  chatType,
  fetchDone,
  email,
  setMessages,
  socketConnectSuccess,
  setSocketConnectSuccess,
}: useWebSocketProps) => {
  const [socketClose, setSocketClose] = useState(false);
  const webSocket = useRef<WebSocket | null>(null);

  const [waitingChat, setWaitingChat] = useState(false);
  const setAnimationState = useChatAvatarStore(
    (state) => state.setAnimationState,
  );

  const updateChatMembers = useCommonChatStore(
    (state) => state.updateChatMembers,
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    return () => {
      webSocket.current?.close();
    };
  }, []);

  useEffect(() => {
    if (!chatRoomId || !email || !chatType) return;
    if (!fetchDone) return;
    if (socketConnectSuccess) return;

    const socket = () => {
      webSocket.current = new WebSocket(import.meta.env.VITE_SOCKET_URL);
      webSocket.current.onopen = () => {
        setSocketConnectSuccess(true);
        setSocketClose(false);

        let messageNewRequestData:
          | ChatJoinRequest
          | GroupChatJoinRequest
          | MissionGameChatJoinRequest;

        switch (chatType) {
          case 'mission':
            messageNewRequestData = {
              action: 'gameChatJoin',
              game_chat_id: Number(chatRoomId),
              email,
            };
            break;
          case 'group':
            messageNewRequestData = {
              action: 'groupchatJoin',
              groupchat_id: chatRoomId!,
              email,
            };
            break;
          case 'personal':
            messageNewRequestData = {
              action: 'chatJoin',
              chatroom_id: chatRoomId!,
              email,
            };
            break;
        }
        SendMessage(webSocket, messageNewRequestData);
      };

      webSocket.current.onmessage = (event: WebSocketEventMap['message']) => {
        try {
          const parsedData: SocketParsedMessage = JSON.parse(event.data);
          if ('chat_id' in parsedData) {
            if (!('id' in parsedData)) return;
            // 유저가 전송했던 채팅 request 메세지
            setMessages((prev) => {
              const parsedSenderId = parsedData.chat_id; // 유저의 이메일 정보

              if (parsedSenderId) {
                const target = prev.some((el) => el.chat_id === parsedSenderId);
                if (target) {
                  return [
                    ...prev.map((el) => {
                      if (el.chat_id === parsedSenderId) {
                        return {
                          ...el,
                          msg: parsedData.msg,
                          id: parsedData?.id || '',
                          send_time: parsedData.send_time,
                          isPending: false,
                          chat_id: parsedSenderId,
                        };
                      }
                      return el;
                    }),
                  ];
                }
              }

              return [
                ...prev,
                {
                  msg: parsedData.msg,
                  id: parsedData.id,
                  send_time: parsedData.send_time,
                  chat_id: parsedSenderId,
                },
              ];
            });
          } else if ('msg' in parsedData && 'motion_url' in parsedData) {
            // Ai에게 받은 response 메세지
            const motionName: ChatAvatarData['animationState'] =
              parsedData.motion_url
                ? {
                    type: 'external',
                    animationName: parsedData.motion_url,
                  }
                : ({ type: 'Idle' } as const);

            setWaitingChat(false);

            const aiSenderId =
              'character_id' in parsedData
                ? (parsedData as unknown as ChatGameMessageResponse)
                    .character_id
                : parsedData.id;

            setMessages((prev) => [
              ...prev,
              {
                msg: parsedData.msg,
                id: aiSenderId,
                send_time: parsedData.send_time,
              },
            ]);

            const expression: ReceivedExpression = parsedData?.expression;
            if (expression) {
              if (
                expression.type &&
                expression.name &&
                expression.duration &&
                expression.value
              ) {
                setAnimationState({
                  id: aiSenderId,
                  state: motionName,
                });
              }
            }
          } else if ('type' in parsedData) {
            // 유저 입, 퇴장 메세지
            const { type, nickname, email } = parsedData;

            if (type) {
              setMessages((prev) => [
                ...prev,
                {
                  msg: `${nickname} ${
                    type === 'enter' ? 'joined' : 'left'
                  } this group`,
                  id: ENTERING_MESSAGE_ID,
                  send_time: '',
                },
              ]);

              updateChatMembers({
                type: type === 'enter' ? 'enter' : 'leave',
                email,
                nickname,
              });
            }
          } else if ('message' in parsedData) {
            // 서버 에러 메세지
            // setMessages((prev) => [
            //   ...prev,
            //   {
            //     msg: parsedData.message,
            //     id: parsedData.requestId,
            //     send_time: 'error',
            //   },
            // ]);
            setWaitingChat(false);
          }
        } catch (error) {
          //
        }
      };
      webSocket.current.onclose = () => {
        setSocketClose(true);
        setSocketConnectSuccess(false);
        queryClient.invalidateQueries({
          queryKey: chatRoomKey.getChatList(),
          refetchType: 'all',
        });
      };
      webSocket.current.onerror = (error: WebSocketEventMap['error']) => {
        setSocketClose(true);
        setSocketConnectSuccess(false);
      };
    };

    socket();
  }, [fetchDone, socketConnectSuccess, email, chatRoomId, chatType]);

  useEffect(() => {
    if (waitingChat) {
      const timer = setTimeout(() => setWaitingChat(false), 1 * MINUTE);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [waitingChat]);

  const sendPing = useCallback(() => {
    if (!webSocket.current) return;

    if (webSocket.current.readyState === WebSocket.OPEN) {
      const jsonData = JSON.stringify({
        action: 'ping',
      });
      webSocket.current.send(jsonData);
    }
  }, []);

  useInterval(sendPing, 30 * SECOND);

  return {
    webSocket,
    socketClose,
    waitingChat,
    setWaitingChat,
  };
};

export default useWebSocket;
